import React, { useState, useRef, useEffect } from "react";
import { TID } from "@/constants/testIds";
import { COPY } from "@/copy";
import { decodeMessage, fetchDecodeStats } from "@/lib/api";
import { toast } from "sonner";
import { OutputCard } from "@/components/OutputCard";

export const Decoder = () => {
  const [mode, setMode] = useState("text");
  const [text, setText] = useState("");
  const [imageData, setImageData] = useState(null);
  const [speaker, setSpeaker] = useState("male");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [decodedCount, setDecodedCount] = useState(20);
  const fileRef = useRef(null);

  useEffect(() => {
    fetchDecodeStats()
      .then((s) => setDecodedCount(s.count || 20))
      .catch(() => {});
  }, []);

  const handleFile = (file) => {
    if (!file) return;
    if (!/(png|jpe?g|webp)/i.test(file.type)) {
      toast.error("Only PNG, JPEG or WEBP");
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      const dataUrl = reader.result;
      const b64 = String(dataUrl).split(",")[1] || "";
      setImageData({ dataUrl, b64, mime: file.type });
    };
    reader.readAsDataURL(file);
  };

  const onDrop = (e) => {
    e.preventDefault();
    const file = e.dataTransfer.files?.[0];
    handleFile(file);
  };

  const onSubmit = async () => {
    if (mode === "text" && !text.trim()) {
      toast.error(COPY.error_input);
      return;
    }
    if (mode === "image" && !imageData) {
      toast.error(COPY.error_input);
      return;
    }
    setLoading(true);
    setResult(null);
    try {
      const payload = {
        text: mode === "text" ? text : null,
        image_base64: mode === "image" ? imageData.b64 : null,
        image_mime: mode === "image" ? imageData.mime : null,
        speaker_gender: speaker,
      };
      const data = await decodeMessage(payload);
      setResult(data);
      // Refresh counter — new decode just added.
      fetchDecodeStats().then((s) => setDecodedCount(s.count || decodedCount)).catch(() => {});
      setTimeout(() => {
        document.getElementById("decoder-output-anchor")?.scrollIntoView({ behavior: "smooth", block: "start" });
      }, 100);
    } catch (e) {
      console.error(e);
      toast.error(COPY.error_decode);
    } finally {
      setLoading(false);
    }
  };

  const onClear = () => {
    setText("");
    setImageData(null);
    setResult(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  return (
    <section
      id="decoder"
      data-testid={TID.decoderSection}
      className="bg-ds-paper ds-border border-l-0 border-r-0 border-t-0"
    >
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 py-20 md:py-28">
        <div className="ds-mono text-xs uppercase tracking-[0.3em] text-ds-flame mb-4">
          {COPY.section_decode}
        </div>
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-12">
          <h2 className="ds-display text-4xl md:text-6xl max-w-3xl">
            {COPY.decode_title}
          </h2>
          <div
            data-testid="decoder-count-badge"
            className="ds-border ds-shadow bg-ds-yellow px-5 py-3 inline-flex items-baseline gap-2 self-start md:self-end"
          >
            <span
              data-testid="decoder-count-number"
              className="ds-display text-3xl md:text-4xl text-ds-ink leading-none"
            >
              {decodedCount.toLocaleString()}+
            </span>
            <span className="ds-mono text-[10px] uppercase tracking-[0.25em] text-ds-ink/80">
              messages decoded
            </span>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-7">
            <div className="ds-border ds-shadow bg-ds-bone p-6 md:p-8">
              <div className="flex ds-border w-fit mb-6">
                <button
                  data-testid={TID.tabText}
                  onClick={() => setMode("text")}
                  className={`px-5 py-2.5 ds-mono text-xs uppercase tracking-widest transition-colors ${mode === "text" ? "bg-ds-ink text-ds-bone" : "bg-ds-bone hover:bg-ds-ink hover:text-ds-bone"}`}
                >
                  {COPY.tab_text}
                </button>
                <div className="w-px bg-ds-ink" />
                <button
                  data-testid={TID.tabImage}
                  onClick={() => setMode("image")}
                  className={`px-5 py-2.5 ds-mono text-xs uppercase tracking-widest transition-colors ${mode === "image" ? "bg-ds-ink text-ds-bone" : "bg-ds-bone hover:bg-ds-ink hover:text-ds-bone"}`}
                >
                  {COPY.tab_image}
                </button>
              </div>

              {mode === "text" ? (
                <textarea
                  data-testid={TID.textInput}
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  placeholder={COPY.placeholder_text}
                  rows={6}
                  className="w-full ds-border bg-ds-paper p-4 ds-mono text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-ds-flame focus:ring-offset-2 focus:ring-offset-ds-bone resize-none"
                />
              ) : (
                <div
                  data-testid={TID.imageDropzone}
                  onDrop={onDrop}
                  onDragOver={(e) => e.preventDefault()}
                  onClick={() => fileRef.current?.click()}
                  className="ds-border border-dashed bg-ds-paper p-10 text-center cursor-pointer hover:bg-ds-bone transition-colors"
                >
                  {imageData ? (
                    <div className="flex flex-col items-center gap-3">
                      <img data-testid={TID.imagePreview} src={imageData.dataUrl} alt="preview" className="max-h-80 ds-border" />
                      <div className="ds-mono text-xs uppercase tracking-widest text-ds-ink/60">click to replace</div>
                    </div>
                  ) : (
                    <div className="flex flex-col items-center gap-2">
                      <div className="ds-mono text-xs uppercase tracking-widest text-ds-flame">+ upload</div>
                      <div className="ds-display text-2xl mt-1">{COPY.drop_text}</div>
                      <div className="ds-mono text-xs uppercase tracking-widest text-ds-ink/50 mt-2">{COPY.drop_hint}</div>
                    </div>
                  )}
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp"
                    onChange={(e) => handleFile(e.target.files?.[0])}
                    className="hidden"
                    data-testid={TID.imageFileInput}
                  />
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                <label className="block">
                  <div className="ds-mono text-[10px] uppercase tracking-[0.25em] text-ds-ink/60 mb-1.5">
                    {COPY.label_speaker}
                  </div>
                  <select
                    data-testid={TID.speakerSelect}
                    value={speaker}
                    onChange={(e) => setSpeaker(e.target.value)}
                    className="w-full ds-border bg-ds-paper px-3 py-2.5 ds-mono text-xs uppercase tracking-widest focus:outline-none focus:ring-2 focus:ring-ds-flame"
                  >
                    <option value="male">{COPY.speaker_male}</option>
                    <option value="female">{COPY.speaker_female}</option>
                    <option value="unknown">{COPY.speaker_unknown}</option>
                  </select>
                </label>
                <div className="hidden md:block" />
              </div>

              <div className="flex flex-wrap gap-3 mt-8">
                <button
                  data-testid={TID.submit}
                  onClick={onSubmit}
                  disabled={loading}
                  className="ds-border bg-ds-flame text-ds-ink px-7 py-3 ds-mono uppercase tracking-widest text-sm font-bold ds-shadow hover:ds-shadow-lg hover:-translate-x-0.5 hover:-translate-y-0.5 transition-all disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? COPY.submitting : COPY.submit} {!loading && "→"}
                </button>
                <button
                  data-testid={TID.clear}
                  onClick={onClear}
                  disabled={loading}
                  className="ds-border bg-ds-paper px-7 py-3 ds-mono uppercase tracking-widest text-sm hover:bg-ds-ink hover:text-ds-bone transition-colors disabled:opacity-50"
                >
                  {COPY.clear}
                </button>
              </div>
            </div>
          </div>

          <div className="lg:col-span-5">
            <div id="decoder-output-anchor" />
            <OutputCard result={result} loading={loading} />
          </div>
        </div>
      </div>
    </section>
  );
};
